"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { ShowcaseCard } from "@/components/showcase-card"
import { DualCTA } from "@/components/dual-cta"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { ShowcaseProject } from "@/types/showcase"

interface ShowcasePageProps {
  initialProjects: ShowcaseProject[]
}

export default function ShowcasePage({ initialProjects }: ShowcasePageProps) {
  const [projects, setProjects] = useState(initialProjects)
  const [mounted, setMounted] = useState(false)

  // Glassmorphism style object for reuse
  const glassmorphismStyle = {
    background: 'rgba(255, 255, 255, 0.21)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: '20px',
    border: '0.25px solid rgba(255, 255, 255, 0.1)',
    boxShadow: `
      0 8px 32px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.5),
      inset 0 -1px 0 rgba(255, 255, 255, 0.1),
      inset 0 0 42px 21px rgba(255, 255, 255, 0.05)
    `
  }

  const GlassmorphismHighlights = () => (
    <>
      {/* Top highlight line */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)'
        }}
      />
      {/* Left highlight line */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '1px',
          height: '100%',
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8), transparent, rgba(255, 255, 255, 0.3))'
        }}
      />
    </>
  )

  useEffect(() => {
    setMounted(true)
    setProjects(initialProjects)
  }, [initialProjects])


  if (!mounted) {
    return null
  }

  return (
    <div className="min-h-screen flex flex-col relative overflow-x-hidden">
      {/* Background Pattern */}
      <div className="fixed inset-0 bg-gradient-to-br from-background via-background to-muted/20" />
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />
      
      {/* Content */}
      <div className="relative z-10 flex-1">
        {/* Hero Section */}
        <section className="py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center space-y-6"
            >
              <Badge className="mb-4 text-sm px-4 py-2">Our Work</Badge>
              <h1 className="text-5xl md:text-6xl font-bold">
                Ideas We&apos;ve Brought to Life
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From concept to reality in days, not months. Check out some of the innovative projects we&apos;ve built for our clients.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            {projects.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {projects.map((project, index) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <ShowcaseCard project={project} />
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center !bg-transparent relative overflow-hidden" style={glassmorphismStyle}>
                <GlassmorphismHighlights />
                <CardContent className="space-y-4">
                  <h3 className="text-2xl font-semibold">Coming Soon</h3>
                  <p className="text-muted-foreground">
                    We&apos;re currently working on some exciting projects. Check back soon to see what we&apos;ve built!
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 md:p-12 !bg-transparent relative overflow-hidden text-center" style={glassmorphismStyle}>
              <GlassmorphismHighlights />
              <CardContent className="space-y-6">
                <h2 className="text-4xl md:text-5xl font-bold">
                  Have an Idea?
                </h2>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  Let&apos;s turn your vision into reality. Book a discovery call and we&apos;ll show you how fast we can build your prototype.
                </p>
                <DualCTA />
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </div>
  )
}